/**
 * studentQuestDetailViewModel — ce que le détail d'une quête (`<StudentQuestDetailPage>`) lit.
 *
 * Patron C (cache seul), comme l'ancien `MyProgressScreen.js` : la quête est cherchée dans
 * `currentUser.object.skill_sets` par l'identifiant de la route (`skillSetId`), aucune lecture ne
 * part. Les quêtes sont là quand on arrive de « Mes quêtes » ; un lien direct avant leur première
 * lecture attend celle que la coquille ou une autre surface a lancée, sinon dit « Quête
 * introuvable » (l'ancien écran restait blanc).
 */
import { createSelector } from "reselect"

import {
    toUIQuestDetail,
    type ApiGameSkill,
    type ApiGameSkillSet,
    type ApiGameStatistics,
} from "../../../adapters/student/shared/questAdapter"
import { type UIQuestDetail } from "../../../ui/types/studentGame"
import { selectDisplayedStudent } from "../../shared/displayedStudent"

export type StudentQuestDetailViewModel = {
    /** `null` tant que la quête n'est pas là, ou quand elle n'existe pas. */
    detail: UIQuestDetail | null
    loading: boolean
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

const selectSkillSets = (state: ReduxState): ApiGameSkillSet[] | undefined => selectDisplayedStudent(state)?.skill_sets
const selectStatistics = (state: ReduxState): ApiGameStatistics | undefined => selectDisplayedStudent(state)?.statistics
const selectSkills = (state: ReduxState): ApiGameSkill[] | undefined => state?.data?.staticData?.skills
const selectSkillSetsRequesting = (state: ReduxState): boolean => !!state?.api?.getSkillSets?.request
const selectSkillSetId = (_state: ReduxState, skillSetId: number | null): number | null => skillSetId

export const studentQuestDetailViewModel = createSelector(
    [selectSkillSets, selectSkills, selectStatistics, selectSkillSetsRequesting, selectSkillSetId],
    (skillSets, skills, statistics, requesting, skillSetId): StudentQuestDetailViewModel => {
        const skillSet = skillSetId != null ? skillSets?.find((candidate) => candidate?.id === skillSetId) : undefined
        const detail = toUIQuestDetail(skillSet, skills, statistics)
        return {
            detail,
            loading: detail == null && (skillSets == null || skills == null) && requesting,
        }
    }
)

/** L'identifiant de la route, lu comme l'ancien écran (`parseInt(route.params?.skillSetId)`). */
export function questDetailIdOf(params: { skillSetId?: unknown } | null | undefined): number | null {
    const id = parseInt(String(params?.skillSetId ?? ""), 10)
    return Number.isNaN(id) ? null : id
}
